import axios, { type InternalAxiosRequestConfig } from 'axios';
import { createSessionRefresher, isAuthRoute, isRefreshSession, tokenExpiryMs } from './session-refresh';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Sent in the background (the offline outbox): a 401 still tries one
     * session refresh, but never signs the person out nor clears the token.
     */
    skipSessionInvalidation?: boolean;
  }
}

/** Extra headers of a contribution (idempotency, recorded-at) and whether it is a background resend. */
export type SubmissionOptions = { headers?: Record<string, string>; background?: boolean };

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

export const sessionRefresher = createSessionRefresher(API_BASE_URL);

// Global session invalidation callback — set by AppProvider on mount
export type AccountStatusReason = 'suspended' | 'banned';

let onSessionInvalidated: ((reason?: AccountStatusReason) => void) | null = null;

export const setSessionInvalidationHandler = (handler: (reason?: AccountStatusReason) => void) => {
  onSessionInvalidated = handler;
};

const RENEW_AHEAD_MS = 30_000;
const tokenExpiresSoon = (token: string) => {
  const expiry = tokenExpiryMs(token);
  return expiry !== null && expiry - Date.now() < RENEW_AHEAD_MS;
};

apiClient.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('yodi-token');
  // Refresh sessions renew a short token ahead of time instead of failing first.
  if (token && isRefreshSession() && !isAuthRoute(config.url) && tokenExpiresSoon(token)) {
    token = (await sessionRefresher.refresh()) ?? token;
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const retried = new WeakSet<InternalAxiosRequestConfig>();

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const config: InternalAxiosRequestConfig | undefined = error.config;
    // A refresh session gets one renewal and one retry before it is over.
    if (status === 401 && config && !retried.has(config) && !isAuthRoute(config.url) && isRefreshSession()) {
      retried.add(config);
      const token = await sessionRefresher.refresh();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return apiClient(config);
      }
    }
    // Only 401 means the session itself is invalid (missing/expired/bad token).
    // A 403 means "authenticated but not allowed for this resource" — logging the
    // user out there kicks them off any page that probes a role-restricted
    // endpoint, even when the caller already handles the failure.
    if (status === 401 && !config?.skipSessionInvalidation) {
      localStorage.removeItem('yodi-token');
      const apiMessage = error.response?.data?.message;
      const normalizedMessage = typeof apiMessage === 'string' ? apiMessage.toLowerCase() : '';
      const reason: AccountStatusReason | undefined = normalizedMessage.includes('banned')
        ? 'banned'
        : normalizedMessage.includes('suspended')
          ? 'suspended'
          : undefined;
      onSessionInvalidated?.(reason);
    }
    return Promise.reject(error);
  },
);
